/**
 * Corporate quote tracking.
 *
 * Pushes two GTM events so corporate leads can be told apart from general enquiries:
 *  - corporate_enquiry_submit: a Klaviyo form inside a quote request section was submitted,
 *    or the built-in quote form came back successfully (form_id: 'shopify_contact').
 *  - corporate_bulk_form_download: a link to the bulk order spreadsheet (.xlsx) was clicked.
 *
 * The built-in form also sends a "Corporate Quote Requested" event to Klaviyo (see below).
 *
 * GTM needs a matching Custom Event trigger for each before anything reaches GA4 or Ads.
 */
(function () {
  if (window.corporateQuoteTracking) return;
  window.corporateQuoteTracking = true;

  window.dataLayer = window.dataLayer || [];

  var DRAFT_KEY = 'basketcase:quote-draft';

  // Klaviyo fires this on window for every onsite form; only count forms inside a quote section.
  window.addEventListener('klaviyoForms', function (event) {
    var detail = event.detail || {};
    if (detail.type !== 'submit' || !detail.formId) return;

    var inQuoteSection = Array.prototype.some.call(
      document.querySelectorAll('[data-corporate-quote]'),
      function (section) {
        return section.querySelector('.klaviyo-form-' + detail.formId) !== null;
      }
    );
    if (!inQuoteSection) return;

    window.dataLayer.push({
      event: 'corporate_enquiry_submit',
      form_id: detail.formId,
    });
  });

  // Until the Klaviyo form is live, the built-in form also sends the enquiry to Klaviyo through
  // the app embed's onsite JS: identify the person (no marketing consent is implied) and track a
  // "Corporate Quote Requested" event that a flow or segment can use. Runs in the capture phase so
  // it goes before Shopify's own submit handling, then re-submits. Capped at 2 seconds so a slow
  // or blocked Klaviyo never stops the enquiry from being sent.
  // Shopify's form object only refills name, email, phone and body after an error, so keep the
  // rest of the answers for restoreDraft. Saved on every submit, before the Klaviyo step below.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target.closest('.corporate-quote__form');
      if (!form) return;
      var fields = {};
      new FormData(form).forEach(function (value, name) {
        if (typeof value === 'string') fields[name] = value;
      });
      try {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ formId: form.id, fields: fields }));
      } catch (error) {
        // Private mode or storage full: an error reload just won't refill these fields.
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target.closest('.corporate-quote__form');
      if (!form || form.dataset.klaviyoSent === 'true') return;

      var klaviyo = window.klaviyo;
      if (!klaviyo || typeof klaviyo.identify !== 'function' || typeof klaviyo.track !== 'function') return;

      event.preventDefault();
      // A second click while Klaviyo is pending would send the event and the form twice.
      if (form.dataset.klaviyoPending === 'true') return;
      form.dataset.klaviyoPending = 'true';
      var submitter = event.submitter;
      if (submitter) submitter.setAttribute('aria-busy', 'true');

      var data = new FormData(form);
      var field = function (name) {
        return String(data.get('contact[' + name + ']') || '').trim();
      };
      var name = field('name').split(/\s+/);

      var profile = {
        $email: field('email'),
        $first_name: name.shift() || '',
        $last_name: name.join(' '),
        $organization: field('Company'),
        'Corporate phone': field('phone'),
      };
      var quote = {
        Company: field('Company'),
        'Number of gifts': field('Number of gifts'),
        'Budget per gift': field('Budget per gift'),
        'Delivering to': field('Delivering to'),
        'Delivery date needed': field('Delivery date needed'),
        'Add branding': field('Add branding'),
        Notes: field('body'),
        Page: window.location.pathname,
      };

      var sent = Promise.resolve()
        .then(function () {
          return klaviyo.identify(profile);
        })
        .then(function () {
          return klaviyo.track('Corporate Quote Requested', quote);
        });
      var timeout = new Promise(function (resolve) {
        setTimeout(resolve, 2000);
      });

      Promise.race([sent, timeout])
        .catch(function () {})
        .then(function () {
          form.dataset.klaviyoSent = 'true';
          delete form.dataset.klaviyoPending;
          if (submitter) submitter.removeAttribute('aria-busy');
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
          } else {
            form.submit();
          }
        });
    },
    true
  );

  function readDraft() {
    try {
      return JSON.parse(window.sessionStorage.getItem(DRAFT_KEY));
    } catch (error) {
      return null;
    }
  }

  function clearDraft() {
    try {
      window.sessionStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      // Nothing saved to clear.
    }
  }

  /** Put the submitted answers back into a form that came back with errors. */
  function restoreDraft(form) {
    var draft = readDraft();
    if (!draft || draft.formId !== form.id || !draft.fields) return;
    Object.keys(draft.fields).forEach(function (name) {
      var value = draft.fields[name];
      Array.prototype.forEach.call(form.elements, function (input) {
        if (input.name !== name || input.type === 'hidden') return;
        if (input.type === 'radio') {
          input.checked = input.value === value;
        } else if (!input.value) {
          // Liquid has already refilled name, email, phone and body; leave those alone.
          input.value = value;
        }
      });
    });
  }

  // The built-in form posts with a full page reload. Bring the result back into view,
  // since the page reloads at the top, and count a successful send as a lead.
  //
  // form.posted_successfully? is true for every contact form on the page, so if another one
  // (a general contact form) shares the page, this form shows the result too. Shopify keeps
  // the posted form's id in the URL hash (the form action is /contact#<id>), so only the form
  // that hash names counts. No hash at all is trusted, rather than losing the lead.
  function handlePostedForm() {
    var posted = window.location.hash.slice(1);
    var results = Array.prototype.filter.call(
      document.querySelectorAll('[data-quote-success], [data-quote-errors]'),
      function (element) {
        return !posted || element.closest('form').id === posted;
      }
    );
    var result = results[0];
    if (!result) return;

    result.scrollIntoView({ block: 'center' });
    result.focus({ preventScroll: true });

    if (!result.hasAttribute('data-quote-success')) {
      restoreDraft(result.closest('form'));
    } else {
      clearDraft();
      window.dataLayer.push({
        event: 'corporate_enquiry_submit',
        form_id: 'shopify_contact',
      });

      // Drop Shopify's ?contact_posted=true so a refresh doesn't count the lead twice.
      var url = new URL(window.location.href);
      if (url.searchParams.has('contact_posted')) {
        url.searchParams.delete('contact_posted');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handlePostedForm);
  } else {
    handlePostedForm();
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href*=".xlsx"]');
    if (!link) return;

    window.dataLayer.push({
      event: 'corporate_bulk_form_download',
      link_url: link.href,
    });
  });
})();
